import { useEffect, useState, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { FiClock, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import 'swiper/css';
import 'swiper/css/navigation';
import BookCard from './BookCard';
import { getRecentlyViewed, clearRecentlyViewed } from '../../utils/recentlyViewed';
import { apiGetBookById } from '../../api';
import { bookCarouselBreakpoints } from '../../utils/carousel';

const RecentlyViewed = ({ excludeId, title = 'Recently Viewed' }) => {
  const [books, setBooks] = useState([]);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    const items = getRecentlyViewed().filter((b) => b.id !== excludeId);
    let cancelled = false;

    // localStorage may contain products viewed before their category was
    // disabled. Revalidate them through the public detail API so hidden
    // categories do not remain visible in Recently Viewed.
    Promise.all(
      items.map((item) =>
        apiGetBookById(item.id)
          .then((data) => data.book)
          .catch(() => null)
      )
    ).then((activeBooks) => {
      if (!cancelled) setBooks(activeBooks.filter(Boolean));
    });

    return () => {
      cancelled = true;
    };
  }, [excludeId]);

  if (books.length === 0) return null;

  const handleClear = () => {
    clearRecentlyViewed();
    setBooks([]);
  };

  return (
    <section className="carousel-section w-full mx-auto px-3 py-3 sm:px-4">
      <div className="carousel-header flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="text-xl sm:text-2xl font-bold text-black flex items-center gap-2">
          <FiClock className="text-orange-600" />
          {title}
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-black hover:text-red-600 flex items-center gap-1 transition mr-2"
            aria-label="Clear recently viewed"
          >
            <FiX /> Clear
          </button>
          <button
            ref={prevRef}
            className="w-9 h-9 rounded-full border border-gray-400 flex items-center justify-center hover:bg-gray-100 transition"
            aria-label="Previous"
          >
            <FiChevronLeft className="text-lg text-black" />
          </button>
          <button
            ref={nextRef}
            className="w-9 h-9 rounded-full border border-gray-400 flex items-center justify-center hover:bg-gray-100 transition"
            aria-label="Next"
          >
            <FiChevronRight className="text-lg text-black" />
          </button>
        </div>
      </div>
      <Swiper
        watchOverflow
        grabCursor
        breakpointsBase="container"
        modules={[Navigation]}
        spaceBetween={16}
        slidesPerView={1}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        breakpoints={bookCarouselBreakpoints}
        className="book-swiper"
      >
        {books.map((book) => (
          <SwiperSlide key={book.id}>
            <BookCard book={book} />
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
};

export default RecentlyViewed;
